import type { TonConnectUI, Wallet, IStorage } from "@tonconnect/ui";
import type { MarketOverview, MarketPort, MarketWalletRequest } from "../domain/market";
import { assertMarketWalletConnection, resolveMarketWallet } from "../domain/market";
import type { Locale } from "../domain/types";
import { type WalletFlowEvent } from "../domain/marketWalletFlow";
import { describeWalletError, walletResponseError, MarketWalletRequestError, type WalletErrorDetails } from "../../shared/marketWalletErrorDetails";

type Context = Pick<MarketOverview, "userId" | "network" | "walletScope">;
/** Do not read, migrate or delete default TON Connect keys from other products. */
export function marketWalletStorage(userId: string, network: string, storage: Pick<Storage, "getItem" | "setItem" | "removeItem">): IStorage {
  const prefix = `diamond-kingdom:wallet:v2:${encodeURIComponent(userId)}:${network}:`;
  return {
    async getItem(key) { return storage.getItem(prefix + key); },
    async setItem(key, value) { storage.setItem(prefix + key, value); },
    async removeItem(key) { storage.removeItem(prefix + key); }
  };
}
let instance: { key: string; ready: Promise<TonConnectUI> } | null = null;
async function walletUi(context: Context, locale: Locale): Promise<TonConnectUI> {
  if (context.walletScope !== "tapper") throw new Error("market_wallet_update_required");
  const key = `${context.userId}:${context.network}`;
  // An identity change must reload the app, never inherit another user's signer.
  if (instance && instance.key !== key) throw new Error("market_wallet_account_changed");
  if (!instance) {
    const ready = import("@tonconnect/ui").then(async ({ TonConnectUI, TonConnect, THEME }) => {
      const connector = new TonConnect({
        manifestUrl: new URL("/api/diamond-tapper/v1/market/manifest", window.location.origin).href,
        storage: marketWalletStorage(context.userId, context.network, window.localStorage)
      });
      connector.setConnectionNetwork(context.network === "mainnet" ? "-239" : "-3");
      const ui = new TonConnectUI({ connector, restoreConnection: false, language: locale,
        uiPreferences: { theme: THEME.DARK, borderRadius: "m" },
        actionsConfiguration: { returnStrategy: "back", modals: ["before"], notifications: [] } });
      await connector.restoreConnection();
      return ui;
    }).catch((error: unknown) => { instance = null; throw error; });
    instance = { key, ready };
  }
  const ui = await instance.ready;
  ui.uiOptions = { language: locale };
  return ui;
}
export async function readMarketWallet(overview: MarketOverview, locale: Locale) {
  if (overview.walletScope !== "tapper") return null;
  const ui = await walletUi(overview, locale);
  return resolveMarketWallet(overview, ui.connected ? ui.account : null);
}
export async function watchMarketWallet(overview: MarketOverview, locale: Locale, changed: () => void) {
  const ui = await walletUi(overview, locale);
  return ui.onStatusChange(changed);
}
export async function connectMarketWallet(api: MarketPort, overview: MarketOverview, locale: Locale, changed: () => void, failed: (error: unknown) => void): Promise<() => void> {
  const ui = await walletUi(overview, locale);
  if (ui.connected) await ui.disconnect();
  ui.setConnectRequestParameters({ state: "loading" });
  const payload = await api.request<{ payload: string; expiresAt: string }>("/wallet/challenge", {});
  ui.setConnectRequestParameters({ state: "ready", value: { tonProof: payload.payload } });
  let active = true, verifying = false;
  let offStatus = () => { /* subscribed below */ }, offModal = () => { /* subscribed below */ };
  const cleanup = () => { active = false; offStatus(); offModal(); window.clearTimeout(timeout); ui.setConnectRequestParameters(null); };
  const fail = (error: unknown) => { if (!active) return; cleanup(); failed(error); };
  const timeout = window.setTimeout(() => fail(new Error("ton_proof_payload_expired")), Math.max(0, Math.min(300000, Date.parse(payload.expiresAt) - Date.now())));
  offStatus = ui.onStatusChange((wallet: Wallet | null) => {
    if (!wallet || !active || verifying) return;
    verifying = true;
    const proof = wallet.connectItems?.tonProof;
    if (!proof || !("proof" in proof)) { fail(new Error("market_wallet_unverified")); return; }
    try { assertMarketWalletConnection(wallet.account, wallet.account.address, overview.network); }
    catch (error) { fail(error); return; }
    void api.request("/wallet/link", { address: wallet.account.address, network: wallet.account.chain, proof: proof.proof, stateInit: wallet.account.walletStateInit, publicKey: wallet.account.publicKey })
      .then(() => { if (active) { cleanup(); changed(); } }).catch(fail);
  }, fail);
  offModal = ui.onModalStateChange(state => { if (state.status === "closed" && state.closeReason === "action-cancelled") fail(new Error("market_wallet_cancelled")); });
  try { await ui.openModal(); } catch (error) { cleanup(); throw error; }
  return () => { cleanup(); ui.closeModal(); };
}
export async function disconnectMarketWallet(api: MarketPort, overview: MarketOverview, locale: Locale) {
  const ui = await walletUi(overview, locale);
  // Revoke server consent first. Failed requests stay retryable, not falsely disconnected.
  await api.request("/wallet/unlink", {});
  if (ui.connected) await ui.disconnect();
}
export type MarketWalletObserver = {
  event(event: WalletFlowEvent): void;
  onRequestSent(reopen: () => void): void;
  now(): number;
  signal?: AbortSignal;
};
export async function sendMarketWalletRequest(request: MarketWalletRequest, overview: MarketOverview, locale: Locale, observer?: MarketWalletObserver) {
  const ui = await walletUi(overview, locale);
  if (!request.from || request.network !== (overview.network === "mainnet" ? "-239" : "-3")) throw new Error("market_submission_invalid");
  if (!resolveMarketWallet(overview, ui.connected ? ui.account : null)) throw new Error("market_connect_wallet");
  const from = assertMarketWalletConnection(ui.account, request.from, overview.network);
  const now = () => observer ? observer.now() : Date.now(), start = now();
  if (request.validUntil * 1000 <= now()) throw new Error("wallet_request_expired");
  const traceId = crypto.randomUUID();
  let responseError: WalletErrorDetails | null = null;
  const emit = (phase: WalletFlowEvent["phase"], details?: WalletErrorDetails) => {
    try {
      observer?.event({ phase, traceId, durationMs: now() - start, remainingMs: request.validUntil * 1000 - now(),
        walletApp: ui.wallet?.device.appName, walletVersion: ui.wallet?.device.appVersion,
        ...details });
    } catch { /* Diagnostic hooks never interrupt wallet signing. */ }
  };
  const visibility = () => emit(document.hidden ? "hidden" : "returned");
  const walletResponse = (event: Event) => {
    const details = walletResponseError((event as CustomEvent<unknown>).detail, traceId);
    if (!details || responseError) return;
    responseError = details; emit("wallet_response", details);
  };
  window.addEventListener("ton-connect-transaction-signing-failed", walletResponse);
  // Retain diagnostics briefly for late answers, but never keep a global listener
  // forever when a wallet doesn't respond. This timer does not cancel payment.
  const responseTimer = window.setTimeout(() => window.removeEventListener("ton-connect-transaction-signing-failed", walletResponse),
    Math.max(0, Math.min(2_147_483_647, request.validUntil * 1000 - now() + 120000)));
  document.addEventListener("visibilitychange", visibility);
  const cleanup = () => document.removeEventListener("visibilitychange", visibility);
  observer?.signal?.addEventListener("abort", cleanup, { once: true });
  emit("sdk_start");
  try {
    // Our Phaser activity and accessible controls explain this state. Hiding the
    // vendor modal prevents an expired request from trapping the whole Mini App.
    const result = await ui.sendTransaction({ ...request, from }, {
      modals: [], notifications: [], returnStrategy: "back", traceId,
      onRequestSent: reopen => {
        emit("bridge_sent");
        try { if (!observer?.signal?.aborted) observer?.onRequestSent(() => {
          emit("wallet_open");
          try { void Promise.resolve(reopen()).catch((error: unknown) => emit("open_failed", describeWalletError(error, "open"))); }
          catch (error) { emit("open_failed", describeWalletError(error, "open")); }
        }); }
        catch { /* The request has already been sent; a UI hook cannot undo it. */ }
      }
    });
    emit("signed"); return result;
  } catch (error) {
    const details = responseError ?? describeWalletError(error);
    emit("failed", details); throw new MarketWalletRequestError(details, error);
  }
  finally {
    cleanup(); observer?.signal?.removeEventListener("abort", cleanup);
    window.clearTimeout(responseTimer); window.removeEventListener("ton-connect-transaction-signing-failed", walletResponse);
  }
}
export async function preflightMarketWallet(address: string, overview: MarketOverview, locale: Locale) {
  const ui = await walletUi(overview, locale);
  if (!resolveMarketWallet(overview, ui.connected ? ui.account : null)) throw new Error("market_connect_wallet");
  assertMarketWalletConnection(ui.account, address, overview.network);
}
export const marketWalletPort = { read: readMarketWallet, watch: watchMarketWallet, connect: connectMarketWallet, disconnect: disconnectMarketWallet, send: sendMarketWalletRequest, preflight: preflightMarketWallet };
export type MarketWalletPort = typeof marketWalletPort;
